import { MouseEvent, forwardRef } from "react";
import { Button, ButtonProps } from "../Button";
import { useDialogContext } from "./utils/useDialogContext";

/**
 * Props for the DialogCancelButton component
 * @extends ButtonProps
 */
export type DialogCancelButtonProps = ButtonProps;

/**
 * DialogCancelButton component for cancel/close actions in a Dialog.
 *
 * Features:
 * - Button that automatically closes the dialog when clicked
 * - Extends all Button component props and functionality
 * - Proper accessibility with close dialog semantics
 * - Automatic focus management
 * - Consistent styling with other dialog elements
 *
 * @example
 * <Dialog.CancelButton appearance="ghost">Cancel</Dialog.CancelButton>
 */
export const DialogCancelButton = forwardRef<
  HTMLButtonElement,
  DialogCancelButtonProps
>((props, ref) => {
  const { onClick, ...rest } = props;
  const { close } = useDialogContext();
  const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    close();
  };
  return (
    <Button
      aria-label="Close dialog"
      data-anv="dialog-cancel-button"
      onClick={onClickHandler}
      ref={ref}
      {...rest}
    />
  );
});

DialogCancelButton.displayName = "DialogCancelButton";
